<template>
  <v-container fluid class="pa-0">
    <!--TOOLBAR-->
    <v-app-bar flat color="red-darken-3">
      <v-app-bar-title class="">
        {{ $t("common_codes.label") }}
      </v-app-bar-title>
      <v-divider class="mx-5 hidden-sm-and-down" inset vertical />
    </v-app-bar>
    <!-- ===================== MOBILE CARD LIST (< 960px) ===================== -->
    <div class="d-md-none common-codes-mobile-list mt-2 px-2 pb-4">
      <v-progress-linear v-if="loading" indeterminate color="red" class="mb-2" />

      <div
        v-for="(item, index) in paginatedParts"
        :key="`${item.oemCode}-${index}`"
        class="common-codes-mobile-card"
      >
        <div class="common-codes-mobile-card__main">
          <div class="d-flex align-center flex-wrap ga-2">
            <v-btn color="black" variant="outlined" size="x-small" rounded readonly @click.stop>
              {{ item.oemCode }}
            </v-btn>
            <v-chip size="small" variant="tonal">
              {{ $t("common_codes.table.count") }}:
              <strong class="ml-1">{{ item.count }}</strong>
            </v-chip>
          </div>

          <div class="text-caption text-grey-darken-1 mt-2">
            <span class="font-weight-medium text-grey-darken-2">
              {{ $t("common_codes.table.category") }}:
            </span>
            {{ item.category?.[$store.getters.language] }}
          </div>
          <div class="text-caption text-grey-darken-1 mt-1">
            <span class="font-weight-medium text-grey-darken-2">
              {{ $t("common_codes.table.title") }}:
            </span>
            {{ item.title?.[$store.getters.language] }}
          </div>
        </div>
      </div>

      <v-alert v-if="!loading && paginatedParts.length < 1" type="info" variant="tonal" class="mt-2">
        No data available.
      </v-alert>
    </div>
    <!-- ===================================================================== -->

    <!-- Desktop table (unchanged) -->
    <v-data-table
      class="d-none d-md-block elevation-1 pb-8"
      :headers="headers"
      :items="parts"
      :loading="loading"
      :items-per-page="-1"
      hide-default-footer
    >
      <template #progress>
        <v-progress-linear color="red" indeterminate />
      </template>
      <template #item="{ item, index }">
        <tr :class="index % 2 === 0 ? 'bg-grey-lighten-4' : ''">
          <td class="text-xs">
            <v-btn variant="outlined" rounded>
              {{ item.oemCode }}
            </v-btn>
          </td>
          <td class="text-xs">
            <v-chip>
              {{ item.count }}
            </v-chip>
          </td>
          <td class="text-xs">
            {{ item.category[$store.getters.language] }}
          </td>
          <td class="text-xs">
            {{ item.title[$store.getters.language] }}
          </td>
        </tr>
      </template>
    </v-data-table>

    <!-- Mobile pagination footer (keep pagination like Inventory) -->
    <div class="common-codes-table-host pb-2">
      <v-data-table
        v-model:options="pagination"
        :headers="headers"
        :items="parts"
        :loading="loading"
        :items-per-page="pagination.itemsPerPage"
        :items-per-page-options="[
          { value: 5, title: '5' },
          { value: 10, title: '10' },
          { value: 25, title: '25' },
          { value: 50, title: '50' },
        ]"
        items-per-page-text="Rows per page"
        class="elevation-1"
        @update:options="updatePagination"
      >
        <template #progress>
          <v-progress-linear color="red" indeterminate />
        </template>
      </v-data-table>
    </div>
  </v-container>
</template>

<script>
import getRequest from "../api/getRequest";

export default {
  data: () => ({
    headers: [
      {
        title: "",
        sortable: false,
        key: "oemCode",
      },
      {
        title: "",
        sortable: false,
        key: "count",
      },
      {
        title: "",
        sortable: false,
        key: "category",
      },
      {
        title: "",
        sortable: false,
        key: "title",
      },
    ],
    loading: false,
    parts: [],
    pagination: {
      itemsPerPage: 10,
      page: 1,
      sortBy: [{ key: "count", order: "desc" }],
      sortDesc: [true],
    },
  }),
  computed: {
    paginatedParts() {
      const items = [...(this.parts || [])];
      const sortBy = this.pagination.sortBy?.[0];
      if (sortBy?.key === "count") {
        items.sort((a, b) => {
          const da = Number(a?.count || 0);
          const db = Number(b?.count || 0);
          return sortBy.order === "desc" ? db - da : da - db;
        });
      }
      const page = this.pagination.page || 1;
      const perPage = this.pagination.itemsPerPage || 10;
      const start = (page - 1) * perPage;
      return items.slice(start, start + perPage);
    },
  },
  watch: {
    "$i18n.locale"() {
      this.getLocale();
    },
  },
  created() {
    this.getLocale();
    this.getParts();
  },
  methods: {
    getLocale() {
      this.headers[0].title = this.$t("common_codes.table.oem");
      this.headers[1].title = this.$t("common_codes.table.count");
      this.headers[2].title = this.$t("common_codes.table.category");
      this.headers[3].title = this.$t("common_codes.table.title");
    },
    getParts() {
      this.loading = true;
      getRequest(
        "/part/inventory/common-codes",
        `role=${this.$store.getters.role}&institution_id=${this.$store.getters.institutionId}`,
      )
        .then((response) => {
          this.parts = response.parts;
          this.pagination.page = 1;
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    updatePagination(options) {
      this.pagination = {
        ...this.pagination,
        ...options,
        itemsPerPage: options.itemsPerPage ? options.itemsPerPage : 10,
        page: options.page ? options.page : 1,
      };
    },
  },
};
</script>

<style scoped>
@media (max-width: 959.98px) {
  .common-codes-table-host :deep(.v-table__wrapper),
  .common-codes-table-host :deep(.v-data-table__wrapper),
  .common-codes-table-host :deep(.v-data-table__wrapper__scroll) {
    display: none !important;
  }
  .common-codes-table-host :deep(.v-data-table-footer) {
    padding-inline: 12px;
  }
}
@media (min-width: 960px) {
  .common-codes-mobile-list,
  .common-codes-table-host {
    display: none !important;
  }
}

.common-codes-mobile-card {
  position: relative;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}
.common-codes-mobile-card__main {
  padding: 10px;
}
</style>
